<script>
import ReportForwardAPI from "@/apis/ReportForwardAPI";
import Common from "./Common";
import RoleSupport from "@/components/RoleSupport";

export default {
  mixins: [Common, RoleSupport],
  mounted: function () {},

  data() {
    return {};
  },
  computed: {
    reportDialogRef: function () {
      return this.$store.state.commonRefs.reportDialogRef;
    },
    commentDialogRef: function () {
      return this.$store.state.commonRefs.commentDialog;
    },
    replyDialogRef: function () {
      return this.$store.state.commonRefs.replyDialog;
    },
    forwardDialogRef: function () {
      return this.$store.state.commonRefs.forwardDialog;
    },
    imageViewerDialogRef: function () {
      return this.$store.state.commonRefs.imageViewerDialog;
    },

    useAutoRead() {
      if (!this.$store.state.profile.settings) {
        return true;
      } else {
        return this.$store.state.profile.settings.use_auto_read == 1;
      }
    },

    reportMap() {
      return this.$store.state.reports;
    },
  },
  methods: {
    //--------------------------------------------
    // Goレスポンス -> 旧表示モデル変換
    //--------------------------------------------
    normalizeEntrySet(entrySet, options) {
      if (!entrySet) {
        return entrySet;
      }
      if (entrySet.report) {
        return entrySet;
      }
      const detail = entrySet.data ? entrySet.data : entrySet;
      return this.buildLegacyEntrySetFromReportDetail(detail, options || {});
    },

    buildLegacyEntrySetFromReportDetail(detail, options) {
      const report = detail || {};
      const author = report.author || {};
      const timeRange = report.timeRange || null;
      const isAllDay = !timeRange;

      const user = options.user || {
        name: author.staffName || author.name || "",
        staff_cd: author.staffCd || author.staff_cd || "",
      };

      const readStaffs = ((report.views && report.views.readStaffs) || []).map(
        (staffCd) => ({ staff_cd: staffCd })
      );
      const totalCount = (report.views && report.views.totalCount) || 0;

      const currentStaffCd = this.account
        ? this.account.staffCd || this.account.staff_cd
        : "";
      const normalizedCurrentStaffCd =
        this.normalizeStaffCdForCompare(currentStaffCd);
      const isAlreadyRead =
        !!normalizedCurrentStaffCd &&
        ((report.views && report.views.readStaffs) || []).some(
          (staffCd) =>
            this.normalizeStaffCdForCompare(staffCd) ===
            normalizedCurrentStaffCd
        );

      return {
        report: {
          id: report.id,
          report_type: report.reportType,
          dt: report.date,
          all_day: isAllDay,
          start_time: timeRange ? timeRange.start : null,
          end_time: timeRange ? timeRange.end : null,
          title: report.title,
          content: report.summary,
          staff_cd: author.staffCd || author.staff_cd || "",
          scope_type: 0,
        },
        user: user,
        tags: (report.tags || []).map((tag) => ({
          tag_id: tag.tagId || tag.tag_id,
          name: tag.name,
        })),
        emotes: this.buildLegacyEmoteRecords(report.emotes),
        comments: (report.comments || []).map((comment) =>
          this.buildLegacyComment(comment, report.id)
        ),
        todo: [],
        files: (report.attachments || []).map(
          (attachment) => attachment.fileId || attachment.file_id
        ),
        forwards: (report.forwards || []).map((forward) => ({
          staff_cd: forward.toStaffCd || forward.to_staff_cd,
          from_staff_cd: forward.fromStaffCd || forward.from_staff_cd,
          message: forward.message,
        })),
        reads: {
          totalCount: totalCount,
          reads: readStaffs,
        },
        extraContents: (report.sections || []).map((section) => ({
          content_type: section.type || section.content_type,
          content: section.text || section.content,
        })),
        goodOrBad: report.attributes ? report.attributes.goodBad : null,
        importance: report.attributes ? report.attributes.importance : null,
        isBookmarked: !!report.isBookmarked,
        isAlreadyRead: isAlreadyRead,
        // 未読・更新はサーバの判定結果をそのまま利用する（SPEC-032）
        isUnread: !!report.isUnread,
        isUpdated: !!report.isUpdated,
        isNew: !!options.isNew,
      };
    },

    buildLegacyEmoteRecords(emotes) {
      if (!emotes) {
        return {
          records: [],
        };
      }
      const records = emotes.map((emote) => {
        const members = emote.members || emote.records || [];
        return {
          emote_cd: emote.emoteCd || emote.emote_cd,
          records: members.map((member) => ({
            staff_cd: member.staffCd || member.staff_cd,
          })),
        };
      });
      return {
        records: records,
      };
    },

    buildLegacyComment(comment, reportId) {
      const author = comment.author || {};
      return {
        comment: {
          id: comment.id,
          report_id: reportId,
          staff_cd: author.staffCd || author.staff_cd || "",
          comment_time: comment.postedAt || comment.comment_time || "",
          content: comment.content,
        },
        user: {
          name: author.staffName || author.name || "",
          staff_cd: author.staffCd || author.staff_cd || "",
        },
        emotes: this.buildLegacyEmoteRecords(comment.emotes),
        replies: (comment.replies || []).map((reply) =>
          this.buildLegacyReply(reply, comment.id)
        ),
      };
    },

    buildLegacyReply(reply, commentId) {
      const author = reply.author || {};
      return {
        reply: {
          id: reply.id,
          comment_id: commentId,
          staff_cd: author.staffCd || author.staff_cd || "",
          reply_time: reply.postedAt || reply.reply_time || "",
          content: reply.content,
        },
        user: {
          name: author.staffName || author.name || "",
          staff_cd: author.staffCd || author.staff_cd || "",
        },
        emotes: this.buildLegacyEmoteRecords(reply.emotes),
      };
    },

    normalizeStaffCd(value) {
      return (value || "").trim();
    },

    normalizeStaffCdForCompare(value) {
      const normalized = (value || "")
        .toString()
        .trim()
        .replace(/[０-９]/g, (c) =>
          String.fromCharCode(c.charCodeAt(0) - 0xfee0)
        );
      if (!normalized) {
        return "";
      }
      if (!/^\d+$/.test(normalized)) {
        return normalized;
      }
      // 会社の担当者CD桁数でゼロ埋めする（SPEC-039。mykk:3, daiwa:5）
      const length = Number(window.app.env.VUE_APP_STAFF_CD_LEN) || 3;
      return normalized.padStart(length, "0");
    },

    toReadStaffCdList(readRecords) {
      if (!Array.isArray(readRecords)) {
        return [];
      }

      const seen = new Set();
      const result = [];
      readRecords.forEach((record) => {
        const staffCd =
          typeof record === "string"
            ? this.normalizeStaffCd(record)
            : this.normalizeStaffCd(record.staff_cd || record.staffCd);
        if (!staffCd || seen.has(staffCd)) {
          return;
        }
        seen.add(staffCd);
        result.push(staffCd);
      });
      return result;
    },

    findGroupByCd(groupCd) {
      if (!groupCd) {
        return null;
      }
      return (
        this.groups.find(
          (group) => (group.group_cd || group.groupCd) === groupCd
        ) || null
      );
    },

    findChiefStaffCd(reportStaffCd, reportGroupCd) {
      let currentGroupCd = reportGroupCd;
      const visited = new Set();

      while (currentGroupCd && !visited.has(currentGroupCd)) {
        visited.add(currentGroupCd);
        const group = this.findGroupByCd(currentGroupCd);
        if (!group) {
          break;
        }

        const chiefStaffCd = this.normalizeStaffCd(
          group.chief_staff_cd || group.chiefStaffCd
        );
        if (chiefStaffCd && chiefStaffCd !== reportStaffCd) {
          return chiefStaffCd;
        }

        currentGroupCd = this.normalizeStaffCd(
          group.parent_group_cd || group.parentGroupCd
        );
      }

      return "";
    },

    buildReadFilterStaffSet(reportStaffCd) {
      const reporterStaffCd = this.normalizeStaffCd(reportStaffCd);
      if (!reporterStaffCd) {
        return null;
      }

      const reportUser = this.findUser(reporterStaffCd);
      if (!reportUser) {
        return null;
      }

      const reportGroupCd = this.normalizeStaffCd(
        reportUser.group_cd || reportUser.groupCd
      );
      if (!reportGroupCd) {
        return null;
      }

      const filterStaffSet = new Set();
      this.users.forEach((user) => {
        const staffCd = this.normalizeStaffCd(user.staff_cd || user.staffCd);
        const groupCd = this.normalizeStaffCd(user.group_cd || user.groupCd);
        if (staffCd && groupCd === reportGroupCd) {
          filterStaffSet.add(staffCd);
        }
      });

      const chiefStaffCd = this.findChiefStaffCd(reporterStaffCd, reportGroupCd);
      if (chiefStaffCd) {
        filterStaffSet.add(chiefStaffCd);
      }

      filterStaffSet.delete(reporterStaffCd);
      return filterStaffSet;
    },

    filterReadStaffCds(readRecords, reportStaffCd, currentStaffCd) {
      const reporterStaffCd = this.normalizeStaffCd(reportStaffCd);
      const viewerStaffCd = this.normalizeStaffCd(currentStaffCd);

      let filtered = this.toReadStaffCdList(readRecords).filter((staffCd) => {
        return staffCd !== reporterStaffCd && staffCd !== viewerStaffCd;
      });

      const filterStaffSet = this.buildReadFilterStaffSet(reporterStaffCd);
      if (filterStaffSet && filterStaffSet.size > 0) {
        filtered = filtered.filter((staffCd) => filterStaffSet.has(staffCd));
      }

      return filtered;
    },

    //--------------------------------------------
    // 表示用データ作成
    //--------------------------------------------

    makeReportDisplayData(entrySet) {
      let self = this;

      let report = entrySet.report;
      let user = entrySet.user;
      let tags = entrySet.tags;
      let emotes = entrySet.emotes;
      let comments = entrySet.comments;
      let todo = entrySet.todo;
      let files = entrySet.files;

      let canForward = this.hasRole("CAN_REPORT_FORWARD");
      let canEdit = report.staff_cd === this.account.staff_cd;

      let isSelfReport = report.staff_cd === this.account.staff_cd;

      //console.log("    title: " + report.title);

      //console.log("makeReportDisplayData entrySet", entrySet);

      let commentRecords = comments.map((cmt) => {
        return self.makeCommentDisplayData(cmt);
      });

      let forwards = entrySet.forwards;

      if (!canForward && !isSelfReport) {
        // 転送権限を持っていない場合は自分向けの転送以外は表示させない
        // ただし、自分の日報の時は閲覧可能とする
        forwards = forwards.filter((f) => {
          return f.staff_cd == this.account.staff_cd;
        });
      }

      const currentStaffCd = this.account
        ? this.account.staffCd || this.account.staff_cd
        : "";
      const filteredReadStaffCds = this.filterReadStaffCds(
        entrySet.reads.reads,
        report.staff_cd,
        currentStaffCd
      );
      let readsStaffs = filteredReadStaffCds.map((staffCd) => {
        const staffName = self.getUserName(staffCd);
        return staffName || staffCd;
      });
      let totalReadCount = entrySet.reads.totalCount;

      let parsedContent = [];
      entrySet.extraContents.forEach((exc) => {
        parsedContent.push(
          this.makeContentStruct(exc.content_type, exc.content)
        );
      });

      if (report.content) {
        parsedContent.push(
          this.makeContentStruct("A0", report.content, "その他")
        );
      }

      let reportEntry = {
        id: report.id,
        entry_type: report.report_type,
        date: report.dt,
        all_day: report.all_day,
        start_time: report.start_time,
        end_time: report.end_time,
        title: report.title,
        content: report.content,
        parsedContentBlocks: parsedContent,
        user_name: user.name,
        goodOrBad: entrySet.goodOrBad,
        importance: entrySet.importance,
        tags: tags,
        emote: self.makeEmoteDisplayData(emotes),
        comments: commentRecords,
        files: files,
        forwards: forwards,
        readsStaffs: readsStaffs,
        totalReads: totalReadCount,
        is_self_report: isSelfReport,
        can_edit: canEdit,
        can_forward: canForward,
        new_arrivals: entrySet.isNew,
        already_read: entrySet.isAlreadyRead,
        unread: entrySet.isUnread,
        updated: entrySet.isUpdated,
        read_event_fired: false,
        is_bookmarked: entrySet.isBookmarked,
      };

      //reportEntry.goodOrBad = 'G';
      //reportEntry.importance = '3';

      if (report.report_type === "B") {
        reportEntry.todoList = todo.map((a) => {
          return {
            title: a.todo.todo.name,
            state: a.status,
            note: a.comment,
          };
        });
      }

      return reportEntry;
    },

    makeContentStruct(contentType, content, title) {
      if (!title) {
        if (contentType == "A1") {
          title = "気付き/課題/提案/バッドニュース";
        } else if (contentType == "A2") {
          title = "ポジティブストーリー（グッドニュース/感謝など）";
        } else if (contentType == "A3") {
          title = "今日のトピックス（業務内容、出張報告、取引先情報など）";
        } else if (contentType == "A4") {
          title = "KPI進捗状況";
        }
      }

      return {
        title: title,
        contents: this.parseReportContent(content),
        contentType: contentType,
      };
    },

    makeCommentDisplayData(commentData) {
      let self = this;

      let commentRec = commentData.comment;
      let commentUser = commentData.user;
      let repliesRecs = commentData.replies;
      let emotes = commentData.emotes;

      let replies = repliesRecs.map((r) => this.makeReplyDisplayData(r));

      //console.log("makeCommentDisplayData commentData", commentData);
      //console.log("makeCommentDisplayData account", this.account);

      let canEdit = commentUser.staff_cd === this.account.staff_cd;

      return {
        comment_id: commentRec.id,
        report_id: commentRec.report_id,
        user_name: commentUser.name,
        staff_cd: commentRec.staff_cd,
        time: commentRec.comment_time,
        content: commentRec.content,
        parsedContent: this.parseReportContent(commentRec.content),
        emote: self.makeEmoteDisplayData(emotes),
        replies: replies,
        can_edit: canEdit,
      };
    },

    makeReplyDisplayData(replyData) {
      let self = this;

      //console.log("makeReplyDisplayData");
      //console.log(replyData);

      let replyRec = replyData.reply;
      let replyUser = replyData.user;
      let emotes = replyData.emotes;

      let canEdit = replyUser.staff_cd === this.account.staff_cd;

      return {
        reply_id: replyRec.id,
        comment_id: replyRec.comment_id,
        staff_cd: replyRec.staff_cd,
        user_name: replyUser.name,
        time: replyRec.reply_time,
        content: replyRec.content,
        parsedContent: this.parseReportContent(replyRec.content),
        emote: self.makeEmoteDisplayData(emotes),
        can_edit: canEdit,
      };
    },

    makeEmoteDisplayData(emoteData) {
      let self = this;

      console.log("emoteData",emoteData);

      if (!emoteData) {
        return {
          emotes: [],
          emote_count: 0,
          popup_data: {},
        };
      }

      let emoteList = [];
      if (Array.isArray(emoteData)) {
        emoteList = emoteData;
      } else if (Array.isArray(emoteData.records)) {
        // 旧形式が渡された場合の安全策
        emoteList = emoteData.records;
      }

      if (!Array.isArray(emoteList) || emoteList.length === 0) {
        return {
          emotes: [],
          emote_count: 0,
          popup_data: {},
        };
      }

      // サマリー情報を作成する
      // ・行われたEmote
      // ・件数

      let totalCount = emoteList.reduce((acc, value, index) => {
        let count = 0;
        if (typeof value.count === "number") {
          count = value.count;
        } else if (Array.isArray(value.members)) {
          count = value.members.length;
        } else if (Array.isArray(value.records)) {
          count = value.records.length;
        }
        return acc + count;
      }, 0);

      let emoteCodes = [];
      let b = emoteList.reduce((acc, value, index) => {
        let cd = 0;
        if (typeof value.emoteCd === "number") {
          cd = value.emoteCd;
        } else if (typeof value.emote_cd === "number") {
          cd = value.emote_cd;
        }
        if (cd > 0 && acc.indexOf(cd) === -1) {
          acc.push(cd);
        }
        return acc;
      }, emoteCodes);

      //console.log(b);

      // ポップアップ用のデータを作成する
      // Emoteの種類
      //   ・・ 毎の登録者
      let popupList = emoteList.map(function (rec) {
        let emoteCd = 0;
        if (typeof rec.emoteCd === "number") {
          emoteCd = rec.emoteCd;
        } else if (typeof rec.emote_cd === "number") {
          emoteCd = rec.emote_cd;
        }

        let members = [];
        if (Array.isArray(rec.members)) {
          members = rec.members;
        } else if (Array.isArray(rec.records)) {
          members = rec.records;
        }

        let staffs = members.map(function (rec2) {
          let staffCd = rec2.staffCd || rec2.staff_cd || "";
          let name = self.getUserName(staffCd);
          //if (!name) {
          //  console.log("getUserNameで取得できませんでした。", staffCd);
          //}
          return name || staffCd;
        });

        return {
          emote_cd: emoteCd,
          staff_names: staffs,
        };
      });

      let popupData = {};
      popupList.forEach(function (p) {
        popupData[p.emote_cd] = p;
      });

      return {
        emotes: b,
        emote_count: totalCount,
        popup_data: popupData,
      };
    },

    // 日報の内容をテキスト、URLで分割します。
    parseReportContent(text) {
      let analyzeResult = [];

      let regexp_url = /(https?:\/\/[a-zA-Z0-9.\-_@:/~?%&;=+#',()*!]+)/g; // ']))/;
      let matchResult = text.match(regexp_url);

      let wkText = text;
      if (matchResult) {
        matchResult.forEach((url) => {
          let urlLength = url.length;
          let startIndex = wkText.indexOf(url);
          if (startIndex > -1) {
            // startIndex の位置までを文字列として切り出す
            let t1 = "";
            if (startIndex > 0) {
              t1 = wkText.substr(0, startIndex);
            }

            // url以降を文字列として切り出す
            let afterIndex = startIndex + urlLength;
            let t2 = wkText.substr(afterIndex);

            analyzeResult.push({
              type: "text",
              value: t1,
            });
            analyzeResult.push({
              type: "url",
              value: url,
            });

            wkText = t2;
          }
        });
      }

      if (wkText) {
        analyzeResult.push({
          type: "text",
          value: wkText,
        });
      }

      return analyzeResult;
    },

    //--------------------------------------------
    // 日報編集
    //--------------------------------------------

    onEditReportClick(entry) {
      let reportDate = entry.date;
      let url = this.buildUrl("/entry/", { dt: reportDate });
      this.$router.push(url);
    },

    //--------------------------------------------
    // コメントダイアログの処理
    //--------------------------------------------

    async onCommentBtnClick(parentEntry, commentEntry) {
      let self = this;

      let report_id = parentEntry.id;
      let entry = parentEntry;
      let comment_id = null;
      let originalMessage = "";
      let staffCd = this.account.staff_cd;

      if (!this.commentDialogRef) {
        console.log("commentDialogがrefsに見つかりませんでした。");
        return;
      }

      let canDelete = false;

      if (commentEntry) {
        comment_id = commentEntry.comment_id;
        originalMessage = commentEntry.content;

        if (!!commentEntry.replies && commentEntry.replies.length > 0) {
          canDelete = false;
        } else {
          canDelete = true;
        }
      }

      let dialogResult = await this.commentDialogRef.open(
        staffCd,
        report_id,
        comment_id,
        originalMessage,
        canDelete,
        {}
      );
      let mode = dialogResult[0];
      let responseData = dialogResult[1];

      if (mode === 1) {
        // 登録/更新APIのレスポンス（Goの CommentResponse）を旧表示モデルへ変換してから表示用データを作成する。
        let savedComment =
          responseData && responseData.data ? responseData.data : responseData;
        let commentData = self.makeCommentDisplayData(
          self.buildLegacyComment(savedComment, report_id)
        );

        self.$store.commit("setReportComment", {
          reportId: report_id,
          commentId: comment_id,
          data: commentData,
        });
      } else if (mode === 9) {
        if (comment_id) {
          let updateIdx = entry.comments.findIndex(
            (v) => v.comment_id == comment_id
          );
          if (updateIdx > -1) {
            entry.comments.splice(updateIdx, 1);
          }

          self.$store.commit("delReportComment", {
            reportId: report_id,
            commentId: comment_id,
          });
        }
      }
    },

    //--------------------------------------------
    // 返信ダイアログの処理
    //--------------------------------------------

    async onReplyBtnClick(parentEntry, replyEntry) {
      let self = this;

      let reportId = parentEntry.report_id;
      let commentId = parentEntry.comment_id;
      let staffCd = this.account.staff_cd;

      let originalMessage = "";
      let updateReplyId = null;
      if (replyEntry) {
        updateReplyId = replyEntry.reply_id;
        originalMessage = replyEntry.content;
      }

      if (!this.replyDialogRef) {
        console.log("replyDialogがrefsに見つかりませんでした。");
        return;
      }

      let dialogResult = await this.replyDialogRef.open(
        staffCd,
        reportId,
        commentId,
        updateReplyId,
        originalMessage,
        {}
      );
      let mode = dialogResult[0];
      let responseData = dialogResult[1];
      if (mode === 1) {
        // 登録/更新APIのレスポンス（Goの ReplyResponse）を旧表示モデルへ変換してから表示用データを作成する。
        let savedReply =
          responseData && responseData.data ? responseData.data : responseData;
        let replyData = self.makeReplyDisplayData(
          self.buildLegacyReply(savedReply, commentId)
        );

        self.$store.commit("setReportReply", {
          reportId: reportId,
          commentId: commentId,
          replyId: updateReplyId,
          data: replyData,
        });
      } else if (mode === 9) {
        self.$store.commit("delReportReply", {
          reportId: reportId,
          commentId: commentId,
          replyId: updateReplyId,
        });
      }
    },

    //--------------------------------------------
    // 転送ダイアログの処理
    //--------------------------------------------

    async onForwardBtnClick(entry) {
      let self = this;
      let reportId = entry.id;

      if (!this.forwardDialogRef) {
        console.log("forwardDialogがrefsに見つかりませんでした。");
        return;
      }

      let dialogResult = await this.forwardDialogRef.open({});

      if (dialogResult[0]) {
        let result = dialogResult[1];
        let message = result.message;
        let forwardStaffs = result.staff_cd_list;

        this.saveForward(reportId, forwardStaffs, message);
      }
    },

    saveForward(reportId, forwardStaffs, comment) {
      let self = this;
      Promise.resolve()
        .then((a) => {
          return this.checkAuthToken();
        })
        .then((a) => {
          return ReportForwardAPI.putForward(reportId, forwardStaffs, comment);
        })
        .then((response) => {
          console.log(response);
          self.showMessageToast("日報を転送しました。");
        })
        .catch(function (err) {
          console.log(err);
          self.showErrorToast("データの登録に失敗しました。");
        });
    },
  },
};
</script>
